import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GlobalSearch } from "@/components/GlobalSearch";
import { AiAssistant } from "@/components/AiAssistant";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Building2, Users, HardHat, Files, UploadCloud, CalendarClock,
  AlertOctagon, FileCheck2, BarChart3, Settings, Sparkles, Bell, Moon, Sun, LogOut, Menu, X,
  ScrollText, Server, Gauge, CalendarCheck, CircleHelp, Headset
} from "lucide-react";
import api from "@/lib/apiClient";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/", testId: "nav-dashboard", end: true },
  { label: "Imprese", icon: Building2, path: "/imprese", testId: "nav-imprese" },
  { label: "Lavoratori", icon: Users, path: "/lavoratori", testId: "nav-lavoratori" },
  { label: "Cantieri", icon: HardHat, path: "/cantieri", testId: "nav-cantieri" },
  { label: "Presenze", icon: CalendarCheck, path: "/presenze", testId: "nav-presenze" },
  { label: "Documenti", icon: Files, path: "/documenti", testId: "nav-documenti" },
  { label: "Carica Documenti", icon: UploadCloud, path: "/carica", testId: "nav-carica-documenti", highlight: true },
  { label: "Scadenziario", icon: CalendarClock, path: "/scadenziario", testId: "nav-scadenziario" },
  { label: "Documenti Mancanti", icon: AlertOctagon, path: "/mancanti", testId: "nav-documenti-mancanti" },
  { label: "Da Verificare", icon: FileCheck2, path: "/da-verificare", testId: "nav-da-verificare" },
  { label: "Report & Export", icon: BarChart3, path: "/report", testId: "nav-report" },
];

const ADMIN_NAV = [
  { label: "Log e Audit", icon: ScrollText, path: "/audit", testId: "nav-audit" },
  { label: "Impostazioni", icon: Settings, path: "/impostazioni", testId: "nav-impostazioni" },
];
const SUPER_NAV = [
  { label: "Consumi AI", icon: Gauge, path: "/consumi-ai", testId: "nav-consumi-ai" },
  { label: "Clienti / Tenant", icon: Server, path: "/tenant", testId: "nav-tenant" },
];

const ROLE_LABELS = { SUPER_ADMIN: "Gestione", TENANT_ADMIN: "Amministratore", TENANT_USER: "Consultazione" };

export function Layout({ children }) {
  const { user, logout, dark, setDark } = useAuth();
  const isAdmin = user && ["SUPER_ADMIN", "TENANT_ADMIN"].includes(user.role);
  const isSuper = user && user.role === "SUPER_ADMIN";
  const navItems = [...NAV, ...(isAdmin ? ADMIN_NAV : []), ...(isSuper ? SUPER_NAV : [])].filter(n => {
    const module = ({"/cantieri":"cantieri", "/lavoratori":"lavoratori", "/presenze":"presenze"})[n.path];
    return (!module || user?.modules?.[module] !== false) && (n.path !== "/presenze" || isAdmin || user?.permissions?.presenze === true);
  });
  const [aiOpen, setAiOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [supportCount,setSupportCount]=useState(0);
  useEffect(() => {let active=true;const load=() => api.get("/support").then(r => {if(active)setSupportCount(r.data.filter(t => t.status !== "risolto").length);}).catch(() => {});load();const timer=setInterval(load,60000);return () => {active=false;clearInterval(timer);};},[]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-[260px] shrink-0 border-r bg-white dark:bg-slate-950 flex flex-col transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b">
          <div className="h-9 w-9 rounded-lg bg-slate-900 dark:bg-blue-600 flex items-center justify-center">
            <HardHat className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-head font-bold text-[15px] leading-tight">CantiereDoc</div>
            <div className="text-[10px] text-muted-foreground tracking-wide">AI Compliance</div>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {navItems.map((n) => (
            <NavLink key={n.path} to={n.path} end={n.end} data-testid={n.testId} onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-900 text-white dark:bg-blue-600" :
                  n.highlight ? "text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800" :
                  "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900"
                }`}>
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}{n.path === "/supporto" && ` (${supportCount})`}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t">
          <button onClick={() => setAiOpen(true)} data-testid="btn-ai-chat-toggle"
            className="w-full flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity">
            <Sparkles className="h-4 w-4" /> Assistente AI
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-20 border-b bg-white/90 dark:bg-slate-950/90 backdrop-blur-md flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="btn-mobile-menu"><Menu className="h-5 w-5" /></button>
          <GlobalSearch />
          <Button variant="ghost" size="icon" title="Aiuto e manuale" aria-label="Aiuto e manuale" onClick={() => navigate("/aiuto")}><CircleHelp className="h-5 w-5" /></Button>
          <Button variant="ghost" size="icon" className="relative" title="Supporto" aria-label="Supporto" onClick={() => navigate("/supporto")}><Headset className="h-5 w-5" />{supportCount > 0 && <span className="absolute -top-1 -right-1 rounded-full bg-blue-600 text-white px-1 text-[10px]">{supportCount}</span>}</Button>
          <TenantSwitcher />
          <div className="ml-auto flex items-center gap-1.5">
            <Button variant="ghost" size="icon" onClick={() => setDark(!dark)} data-testid="btn-theme-toggle">
              {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>
            <DropdownMenu onOpenChange={(o) => o && loadNotifs()}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="btn-notifications">
                  <Bell className="h-[18px] w-[18px]" />
                  {unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">{unread}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-96 max-h-[70vh] overflow-y-auto">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel className="p-0">Notifiche ({notifs.length})</DropdownMenuLabel>
                  {unread > 0 && <button onClick={markAll} className="text-xs text-blue-600" data-testid="btn-mark-all-read">Segna lette</button>}
                </div>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <div className="p-4 text-sm text-muted-foreground">Nessuna notifica.</div>}
                {notifs.slice(0, 30).map((n) => (
                  <DropdownMenuItem key={n.id} onClick={() => navigate("/scadenziario")}
                    className={`flex flex-col items-start gap-0.5 py-2 ${!n.read ? "bg-blue-50/50 dark:bg-slate-900" : ""}`}>
                    <span className={`text-[11px] font-semibold badge-${n.severity} rounded px-1.5`}>{n.severity === "ROSSO" ? "Urgente" : "Attenzione"}</span>
                    <span className="text-xs">{n.message}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-900" data-testid="btn-user-menu">
                  <div className="h-8 w-8 rounded-full bg-slate-900 dark:bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                    {(user?.name || "U").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="hidden md:block text-left">
                    <div className="text-xs font-semibold leading-tight">{user?.name}</div>
                    <div className="text-[10px] text-muted-foreground">{ROLE_LABELS[user?.role]}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuLabel className="text-xs text-muted-foreground">Cliente di appartenenza: {user?.tenant_name || "Gestione generale"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); logout(); }} data-testid="btn-logout"><LogOut className="h-4 w-4 mr-2" /> Esci</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>

      <AiAssistant open={aiOpen} onOpenChange={setAiOpen} />
    </div>
  );
}

function TenantSwitcher() {
  const { isSuper, actingTenant, setActingTenant } = useAuth();
  const [tenants, setTenants] = useState([]);
  useEffect(() => { if (isSuper) api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {}); }, [isSuper]);
  if (!isSuper) return null;
  const change = (v) => { setActingTenant(v === "all" ? "" : v); window.location.reload(); };
  return (
    <div className="flex items-center gap-2" data-testid="tenant-switcher">
      <span className="hidden md:inline text-xs text-muted-foreground">Cliente:</span>
      <Select value={actingTenant || "all"} onValueChange={change}>
        <SelectTrigger className={`h-9 w-44 lg:w-60 ${actingTenant ? "border-blue-500 ring-1 ring-blue-500" : ""}`} data-testid="tenant-switcher-trigger"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti i clienti (aggregato)</SelectItem>
          {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.company_name || t.name || t.id}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
